// Reusable async-data hook for the SPA (established in W2 for the catalog
// screens; W3+ modules reuse it verbatim).
//
//   const { data, loading, error, reload } = useResource(
//     () => catalog.listItems({ type, q }),   // a thunk returning a Promise
//     [type, q],                              // deps — refetch when these change
//   );
//
// Contract:
//   - Fetches on mount and whenever any dep changes (standard useEffect deps).
//   - `loading` is true during the in-flight request; `data` holds the last
//     successful value; `error` holds the ApiError.message on failure.
//   - `reload()` re-runs the current fetcher (used by the retry button and after
//     a successful create/edit/activate write).
//   - Stale responses are ignored: if deps change (or the component unmounts)
//     before a request resolves, its result/error is dropped.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./apiClient.js";

export function useResource(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bump on every reload/dep-change so only the latest run may commit state.
  const runId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fetcher, deps);

  const load = useCallback(async () => {
    const myRun = ++runId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await run();
      if (mounted.current && myRun === runId.current) {
        setData(result);
        setLoading(false);
      }
    } catch (e) {
      if (mounted.current && myRun === runId.current) {
        const msg =
          e instanceof ApiError
            ? e.message
            : "Something went wrong loading this data.";
        setError(msg);
        setLoading(false);
      }
    }
  }, [run]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
